import { useState } from "react"
import {
  ChevronDownIcon,
  PencilSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import { useGetProductFeedMediaQuery } from "~/ui-rtk/api/supplementalFeedApi"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import { useCubeAvailability } from "~/ui-rtk/hooks/cube"
import { formatNumber } from "~/ui-rtk/utils/format"
import useProductVariantsQuery, {
  VARIANTS_CUBE,
  type TProductVariant,
  type TVariantGroup,
} from "../hooks/variants-query"
import FeedMediaSection from "./FeedMediaSection"

const price = (variant: TProductVariant) => {
  if (variant.price === null) return "—"
  const current = formatNumber(variant.salePrice ?? variant.price, {
    currency: true,
  })
  return variant.salePrice !== null
    ? `${current} (was ${formatNumber(variant.price, { currency: true })})`
    : current
}

/** One size of the color: stock and price, read-only. */
function SizeChip({ variant }: { variant: TProductVariant }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-system-md border border-basic-blue px-2 py-0.5 text-xs text-gray-300"
      title={`${variant.title}${variant.sku ? ` · SKU ${variant.sku}` : ""} · ${price(variant)}`}
    >
      <span className="font-medium text-white">
        {variant.size || variant.title || variant.id}
      </span>
      <span className={variant.available ? "text-success" : "text-error"}>
        {variant.available ? variant.quantity : "sold out"}
      </span>
    </span>
  )
}

/**
 * One color of the product: its own image, the sizes with stock, and —
 * expanded — the feed media editor keyed by the color ref
 * (`<productId>_<color>`). One selection per color; the CSV writes it onto
 * every size of that color. Sizes are never edited one by one.
 */
function ColorCard({
  group,
  open,
  onToggle,
}: {
  group: TVariantGroup
  open: boolean
  onToggle: () => void
}) {
  const label = group.color || "Default"
  const editable = group.colorRef.length > 0
  // The color's current selection, so the card can say "add" vs "edit (N)"
  // before the editor is opened. Same query the editor runs — RTK dedupes.
  const { data: media } = useGetProductFeedMediaQuery(
    { productRefId: group.colorRef },
    { skip: !editable },
  )
  const selected = media
    ? media.customImages.length + media.customVideos.length
    : 0
  // One CTA, three faces: a bright "Add" when the color has nothing yet,
  // a quiet "Edit (N)" when it does, "Hide" while the editor is open.
  const quiet =
    "btn btn-xs w-fit gap-1 border border-system-blue-600 bg-system-blue-700 text-white hover:bg-system-blue-600"
  let cta = {
    label: "Add feed media",
    Icon: PlusIcon,
    className:
      "btn btn-xs w-fit gap-1 border-none bg-brand text-white hover:bg-brand/80",
  }
  if (open) {
    cta = { label: "Hide feed media", Icon: ChevronDownIcon, className: quiet }
  } else if (selected > 0) {
    cta = {
      label: `Edit feed media (${selected})`,
      Icon: PencilSquareIcon,
      className: quiet,
    }
  }

  return (
    <div className="rounded-system-md border border-basic-blue bg-basic-black p-3">
      <div className="flex items-start gap-3">
        {group.imageUrl ? (
          <img
            src={group.imageUrl}
            alt={label}
            loading="lazy"
            className="h-20 w-20 shrink-0 rounded-system-md border border-basic-blue object-contain"
          />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-system-md border border-dashed border-system-gray-800 text-[10px] text-gray-500">
            no image
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-white">{label}</span>
            {group.available ? (
              <Chip className="bg-success/20 text-success">In stock</Chip>
            ) : (
              <Chip className="bg-error/20 text-error">Out of stock</Chip>
            )}
            <span className="text-xs text-gray-400">
              {group.variants.length} size
              {group.variants.length === 1 ? "" : "s"} ·{" "}
              {formatNumber(group.quantity)} in stock
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {group.variants.map(variant => (
              <SizeChip key={variant.id} variant={variant} />
            ))}
          </div>
          {editable ? (
            <button
              type="button"
              onClick={onToggle}
              title="Pick the feed image / video for this color — every size gets it"
              className={cta.className}
            >
              <cta.Icon className="h-3 w-3" />
              {cta.label}
            </button>
          ) : (
            <p className="text-xs text-gray-500">
              No color option — this product&apos;s Feed Media above applies.
            </p>
          )}
        </div>
      </div>
      {editable && open && (
        <div className="mt-3 border-t border-basic-blue pt-3">
          <FeedMediaSection productRefId={group.colorRef} />
        </div>
      )}
    </div>
  )
}

/**
 * A product's variants, grouped by color (MD-5394): one card per color with
 * that color's own image, its sizes, and one feed media editor per color.
 * The selection is keyed by the color ref and ships as a CSV row for every
 * size of that color, so a variant-keyed catalog gets media per color.
 *
 * The Mega Feed table stays one row per product; this is the only place the
 * variant grain surfaces.
 */
export default function VariantsSection({
  itemGroupId,
}: {
  itemGroupId: string
}) {
  const { groups, isLoading, error } = useProductVariantsQuery(itemGroupId)
  // useCubeQuery silently skips a cube db_capabilities doesn't list for this
  // company — which reads as "no variants" unless said so. Name the cause.
  const { isLoaded: capabilitiesLoaded, isAvailable } =
    useCubeAvailability(VARIANTS_CUBE)
  const [openColorRef, setOpenColorRef] = useState<string | null>(null)

  if (!capabilitiesLoaded || isLoading) {
    return <span className="loading loading-spinner loading-sm" />
  }
  if (!isAvailable) {
    return (
      <p className="text-xs text-gray-400">
        The variant feed table (product_feed__shopify__by_product_variant) is
        not in this company&apos;s snapshot, or the Cube server has not loaded
        the variant model yet.
      </p>
    )
  }
  if (error) {
    return (
      <p className="text-xs text-error">
        Could not load the variants: {error.toString()}
      </p>
    )
  }
  if (groups.length === 0) {
    return (
      <p className="text-xs text-gray-400">
        No variants in the feed snapshot for this product.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {groups.map(group => (
        <ColorCard
          key={group.colorRef || "__default"}
          group={group}
          open={openColorRef === group.colorRef}
          onToggle={() =>
            setOpenColorRef(current =>
              current === group.colorRef ? null : group.colorRef,
            )
          }
        />
      ))}
    </div>
  )
}
